import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  ThumbsUp, ThumbsDown, Plus, Search, User, Award, AlertTriangle,
  TrendingUp, TrendingDown, BarChart3, Calendar, Filter, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BehaviorTrackingPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('incidents');
  
  // Data states
  const [incidents, setIncidents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [students, setStudents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  
  // Filter states
  const [filterType, setFilterType] = useState('all');
  const [searchStudent, setSearchStudent] = useState('');
  
  // Dialog states
  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  
  // Form state
  const [incidentForm, setIncidentForm] = useState({
    student_id: '',
    category_id: '',
    incident_type: 'positive',
    title: '',
    description: '',
    points: 5,
    action_taken: '',
    incident_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      
      const [incidentsRes, categoriesRes, studentsRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/behavior/incidents`, { headers }).catch(e => ({ data: [] })),
        axios.get(`${API_URL}/api/behavior/categories`, { headers }).catch(e => ({ data: [] })),
        axios.get(`${API_URL}/api/behavior/students-list`, { headers }).catch(e => ({ data: [] })),
        axios.get(`${API_URL}/api/behavior/analytics`, { headers }).catch(e => ({ data: null }))
      ]);
      
      setIncidents(incidentsRes.data || []);
      setCategories(categoriesRes.data || []);
      const studentsData = studentsRes.data?.items || studentsRes.data || [];
      setStudents(studentsData);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load behavior data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIncident = async () => {
    if (!incidentForm.student_id || !incidentForm.category_id || !incidentForm.title) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/behavior/incidents`, incidentForm, {
        headers: getAuthHeader()
      });
      toast.success('Behavior incident recorded!');
      setIncidentDialogOpen(false);
      setIncidentForm({
        student_id: '',
        category_id: '',
        incident_type: 'positive',
        title: '',
        description: '',
        points: 5,
        action_taken: '',
        incident_date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to record incident');
    }
  };

  const handleDeleteIncident = async (incidentId) => {
    if (!window.confirm('Are you sure you want to delete this incident?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/behavior/incidents/${incidentId}`, {
        headers: getAuthHeader()
      });
      toast.success('Incident deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete incident');
    }
  };

  const viewStudentProfile = async (studentId) => {
    try {
      const response = await axios.get(`${API_URL}/api/behavior/student/${studentId}`, {
        headers: getAuthHeader()
      });
      setStudentProfile(response.data);
      setProfileDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load student profile');
    }
  };

  const handleCategorySelect = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      setIncidentForm({
        ...incidentForm,
        category_id: categoryId,
        incident_type: category.incident_type,
        points: category.default_points,
        title: category.name
      });
    }
  };

  const filteredIncidents = incidents.filter(incident => {
    if (filterType !== 'all' && incident.incident_type !== filterType) return false;
    if (searchStudent && !incident.student_name?.toLowerCase().includes(searchStudent.toLowerCase())) return false;
    return true;
  });

  const getGradeColor = (grade) => {
    const colors = {
      'Excellent': 'bg-green-100 text-green-800',
      'Good': 'bg-blue-100 text-blue-800',
      'Average': 'bg-yellow-100 text-yellow-800',
      'Needs Improvement': 'bg-orange-100 text-orange-800',
      'Poor': 'bg-red-100 text-red-800'
    };
    return colors[grade] || 'bg-gray-100 text-gray-800';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Student Behavior Tracking</h1>
            <p className="text-muted-foreground">Track and manage student behavior with merit/demerit points</p>
          </div>
          <Dialog open={incidentDialogOpen} onOpenChange={setIncidentDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-incident-btn">
                <Plus className="h-4 w-4 mr-2" /> Record Incident
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Record Behavior Incident</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {/* Student Selection */}
                <div className="space-y-1">
                  <Label className="text-sm">Student *</Label>
                  <Select value={incidentForm.student_id} onValueChange={(v) => setIncidentForm({...incidentForm, student_id: v})}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select student" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map(student => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.name} ({student.admission_no || student.student_id}) {student.class_name ? `- ${student.class_name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Incident Type */}
                <div className="space-y-1">
                  <Label className="text-sm">Incident Type</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={incidentForm.incident_type === 'positive' ? 'default' : 'outline'}
                      className={incidentForm.incident_type === 'positive' ? 'bg-green-600 hover:bg-green-700' : ''}
                      onClick={() => setIncidentForm({...incidentForm, incident_type: 'positive', points: Math.abs(incidentForm.points)})}
                    >
                      <ThumbsUp className="h-4 w-4 mr-1" /> Positive
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={incidentForm.incident_type === 'negative' ? 'default' : 'outline'}
                      className={incidentForm.incident_type === 'negative' ? 'bg-red-600 hover:bg-red-700' : ''}
                      onClick={() => setIncidentForm({...incidentForm, incident_type: 'negative', points: -Math.abs(incidentForm.points)})}
                    >
                      <ThumbsDown className="h-4 w-4 mr-1" /> Negative
                    </Button>
                  </div>
                </div>

                {/* Category & Title in row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm">Category *</Label>
                    <Select value={incidentForm.category_id} onValueChange={handleCategorySelect}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories
                          .filter(c => c.incident_type === incidentForm.incident_type)
                          .map(category => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name} ({category.default_points > 0 ? '+' : ''}{category.default_points})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Title *</Label>
                    <Input
                      className="h-9"
                      value={incidentForm.title}
                      onChange={(e) => setIncidentForm({...incidentForm, title: e.target.value})}
                      placeholder="Brief title"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-1">
                  <Label className="text-sm">Description (Optional)</Label>
                  <Textarea
                    value={incidentForm.description}
                    onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})}
                    placeholder="Details..."
                    rows={2}
                  />
                </div>

                {/* Points & Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm">Points</Label>
                    <Input
                      className="h-9"
                      type="number"
                      value={Math.abs(incidentForm.points)}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setIncidentForm({
                          ...incidentForm,
                          points: incidentForm.incident_type === 'positive' ? val : -val
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Date</Label>
                    <Input
                      className="h-9"
                      type="date"
                      value={incidentForm.incident_date}
                      onChange={(e) => setIncidentForm({...incidentForm, incident_date: e.target.value})}
                    />
                  </div>
                </div>

                {/* Action Taken (for negative) */}
                {incidentForm.incident_type === 'negative' && (
                  <div className="space-y-1">
                    <Label className="text-sm">Action Taken</Label>
                    <Select value={incidentForm.action_taken} onValueChange={(v) => setIncidentForm({...incidentForm, action_taken: v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select action (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="detention">Detention</SelectItem>
                        <SelectItem value="parent_meeting">Parent Meeting</SelectItem>
                        <SelectItem value="counselor">Counselor Referral</SelectItem>
                        <SelectItem value="suspension">Suspension</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setIncidentDialogOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={handleCreateIncident}>Record Incident</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Incidents</p>
                    <p className="text-2xl font-bold">{analytics.total_incidents}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Positive</p>
                    <p className="text-2xl font-bold text-green-700">{analytics.positive_incidents}</p>
                  </div>
                  <ThumbsUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Negative</p>
                    <p className="text-2xl font-bold text-red-700">{analytics.negative_incidents}</p>
                  </div>
                  <ThumbsDown className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Categories</p>
                    <p className="text-2xl font-bold">{categories.length}</p>
                  </div>
                  <Award className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="incidents">Recent Incidents</TabsTrigger>
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
          </TabsList>

          {/* Incidents Tab */}
          <TabsContent value="incidents" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle>Recent Behavior Incidents</CardTitle>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-none">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search student..."
                        value={searchStudent}
                        onChange={(e) => setSearchStudent(e.target.value)}
                        className="pl-9 w-full sm:w-[200px]"
                      />
                    </div>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-[130px]">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="positive">Positive</SelectItem>
                        <SelectItem value="negative">Negative</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : filteredIncidents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No incidents recorded yet. Click "Record Incident" to add one.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredIncidents.map((incident) => (
                      <motion.div
                        key={incident.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-4 rounded-lg border ${
                          incident.incident_type === 'positive' 
                            ? 'border-green-200 bg-green-50/50' 
                            : 'border-red-200 bg-red-50/50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-full ${
                              incident.incident_type === 'positive' ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                              {incident.incident_type === 'positive' 
                                ? <ThumbsUp className="h-4 w-4 text-green-600" />
                                : <ThumbsDown className="h-4 w-4 text-red-600" />
                              }
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="link" 
                                  className="p-0 h-auto font-semibold"
                                  onClick={() => viewStudentProfile(incident.student_id)}
                                >
                                  {incident.student_name}
                                </Button>
                                <Badge variant="outline" className="text-xs">
                                  {incident.class_name}
                                </Badge>
                              </div>
                              <p className="font-medium mt-1">{incident.title}</p>
                              {incident.description && (
                                <p className="text-sm text-muted-foreground mt-1">{incident.description}</p>
                              )}
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {incident.incident_date}
                                </span>
                                <span>By: {incident.reporter_name}</span>
                                {incident.action_taken && (
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {incident.action_taken.replace('_', ' ')}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={`${
                              incident.points > 0 
                                ? 'bg-green-600' 
                                : 'bg-red-600'
                            }`}>
                              {incident.points > 0 ? '+' : ''}{incident.points} pts
                            </Badge>
                            {(user?.role === 'school_admin' || user?.role === 'super_admin') && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteIncident(incident.id)}
                              >
                                <Trash2 className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Positive */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <TrendingUp className="h-5 w-5" /> Top Merit Students
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.top_positive_students?.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.top_positive_students.map((student, index) => (
                        <div key={student.student_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium">{student.name}</p>
                              <p className="text-xs text-muted-foreground">{student.admission_no}</p>
                            </div>
                          </div>
                          <Badge className="bg-green-600">+{student.points} pts</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">No data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Negative */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <TrendingDown className="h-5 w-5" /> Needs Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.top_negative_students?.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.top_negative_students.filter(s => s.points < 0).map((student, index) => (
                        <div key={student.student_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center font-bold">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium">{student.name}</p>
                              <p className="text-xs text-muted-foreground">{student.admission_no}</p>
                            </div>
                          </div>
                          <Badge className="bg-red-600">{student.points} pts</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">No negative incidents</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Positive Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <ThumbsUp className="h-5 w-5" /> Positive Behaviors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {categories.filter(c => c.incident_type === 'positive').map(category => (
                      <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{category.name}</p>
                          {category.description && (
                            <p className="text-xs text-muted-foreground">{category.description}</p>
                          )}
                        </div>
                        <Badge className="bg-green-600">+{category.default_points}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Negative Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <ThumbsDown className="h-5 w-5" /> Negative Behaviors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {categories.filter(c => c.incident_type === 'negative').map(category => (
                      <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{category.name}</p>
                          {category.description && (
                            <p className="text-xs text-muted-foreground">{category.description}</p>
                          )}
                        </div>
                        <Badge className="bg-red-600">{category.default_points}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Student Profile Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Student Behavior Profile</DialogTitle>
            </DialogHeader>
            {studentProfile && (
              <div className="space-y-6">
                {/* Student Info */}
                <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className="text-xl">
                      {studentProfile.student.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold">{studentProfile.student.name}</h3>
                    <p className="text-muted-foreground">
                      {studentProfile.student.admission_no} • {studentProfile.student.class_name}
                    </p>
                  </div>
                  <div className="text-center">
                    <Badge className={`text-lg px-4 py-2 ${getGradeColor(studentProfile.stats.grade)}`}>
                      {studentProfile.stats.grade}
                    </Badge>
                    <p className="text-2xl font-bold mt-2">
                      {studentProfile.stats.total_points > 0 ? '+' : ''}{studentProfile.stats.total_points}
                    </p>
                    <p className="text-xs text-muted-foreground">Total Points</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">+{studentProfile.stats.positive_points}</p>
                    <p className="text-xs text-muted-foreground">Merit Points</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{studentProfile.stats.negative_points}</p>
                    <p className="text-xs text-muted-foreground">Demerit Points</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{studentProfile.stats.positive_count}</p>
                    <p className="text-xs text-muted-foreground">Positive</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{studentProfile.stats.negative_count}</p>
                    <p className="text-xs text-muted-foreground">Negative</p>
                  </div>
                </div>

                {/* Recent Incidents */}
                <div>
                  <h4 className="font-semibold mb-3">Recent Incidents</h4>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {studentProfile.incidents.map(incident => (
                      <div 
                        key={incident.id} 
                        className={`p-3 rounded-lg border ${
                          incident.incident_type === 'positive' 
                            ? 'border-green-200 bg-green-50/50' 
                            : 'border-red-200 bg-red-50/50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{incident.title}</p>
                            <p className="text-xs text-muted-foreground">{incident.incident_date}</p>
                          </div>
                          <Badge className={incident.points > 0 ? 'bg-green-600' : 'bg-red-600'}>
                            {incident.points > 0 ? '+' : ''}{incident.points}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BehaviorTrackingPage;
